import { fromJS, List } from 'immutable'
import { get, set, merge } from 'utils/immutable'
import { prop, compose } from 'ramda'
import {
  RPWF_START_WORKFLOW,
  RPWF_PAGINATE_SEARCH_RESULTS,
  RPWF_SHOW_PINS_NOT_AVAILABLE_MODAL,
  RPWF_SHOW_SERVICE_RECORD_MODAL,
  RPWF_SHOW_CONFIRM_CIRCUIT_MAPPING_MODAL,
  RPWF_SET_SEARCH_RESULTS,
  RPWF_RESET_WORKFLOW,
  RPWF_SET_STAGED_RPS,
  RPWF_RESET_SEARCH,
  RPWF_REMOVE_STAGED_RP,
  RPWF_SET_SELECTED_RPS,
  RPWF_SET_IS_SEARCHING,
  RPWF_SET_IS_ADDING_RP,
  RPWF_SET_VIEW,
  RPWF_SET_IS_ADDING_COLLECTION,
  RPWF_SET_COLLECTIONS,
  RPWF_SET_IS_STAGING_RPS,
  RPWF_SET_COMMIT_NOTE,
  RPWF_RESET_ADD_COLLECTION,
  RPWF_SET_CURRENT_COLLECTION,
  RPWF_SET_RESOURCE_LIMITS_ERROR,
  RPWF_SET_SEARCH_VALUE,
} from 'actions/addReferenceParametersWorkflow'
import { GET_RPS_SUCCESS } from 'actions/referenceParameter'
import { SEARCH } from 'utils/constants'
import { isArray } from 'utils/types'
import { createReducer } from './utils'

const MAX_RP_SEARCH_COUNT = 10
const SEARCH_INDEX_START = 1

const initialState = fromJS({
  started: false,
  serialNumber: '',
  selectedRPs: [],
  isAddingRPs: false,
  serviceRecordModal: {
    visible: false,
  },
  pinsNotAvailableModal: {
    visible: false,
    circuitsByRP: [],
  },
  confirmCircuitMappingModal: {
    visible: false,
  },
  search: {
    search: '',
    count: MAX_RP_SEARCH_COUNT,
    startIndex: SEARCH_INDEX_START,
    referenceParameters: [],
    isSearching: false,
    showLoadingOverlay: false,
    total: 'undefined',
  },
  stagedRPs: [],
  view: SEARCH,
  isAddingCollection: false,
  collections: [],
  isLoadingCollections: true,
  isStagingRPs: false,
  commitNote: '',
  currentCollection: {},
  resouceLimitsError: '',
})

const handlers = {
  [RPWF_START_WORKFLOW]: startAddRPWorkflow,
  [RPWF_SHOW_SERVICE_RECORD_MODAL]: showServiceRecordModal,
  [RPWF_SHOW_PINS_NOT_AVAILABLE_MODAL]: showPinsNotAvailableModal,
  [RPWF_SHOW_CONFIRM_CIRCUIT_MAPPING_MODAL]: showConfirmCircuitMappingModal,
  [RPWF_SET_SEARCH_RESULTS]: setPRSearchResults,
  [RPWF_RESET_WORKFLOW]: resetAddRPWorkflow,
  [RPWF_SET_STAGED_RPS]: setStagedRPs,
  [RPWF_RESET_SEARCH]: resetSearch,
  [RPWF_REMOVE_STAGED_RP]: removeStagedRP,
  [RPWF_SET_SELECTED_RPS]: setSelectedRPs,
  [RPWF_SET_IS_SEARCHING]: setIsSearching,
  [RPWF_SET_IS_ADDING_RP]: setIsAddingRP,
  [RPWF_PAGINATE_SEARCH_RESULTS]: setIsPaginating,
  [RPWF_SET_VIEW]: setView,
  [RPWF_SET_IS_ADDING_COLLECTION]: setIsAddingCollection,
  [RPWF_SET_COLLECTIONS]: getCollectionsSuccess,
  [RPWF_SET_IS_STAGING_RPS]: setIsStagingRPs,
  [RPWF_SET_COMMIT_NOTE]: setCommitNote,
  [RPWF_RESET_ADD_COLLECTION]: resetAddCollection,
  [GET_RPS_SUCCESS]: getRPsSuccess,
  [RPWF_SET_CURRENT_COLLECTION]: setCurrentCollection,
  [RPWF_SET_RESOURCE_LIMITS_ERROR]: setResourceLimitsError,
  [RPWF_SET_SEARCH_VALUE]: setSearchValue,
}

export default createReducer(initialState, handlers)

function setResourceLimitsError(state, { error = '' }) {
  return state.set('resourceLimitsError', error)
}

function setSearchValue(state, { value }) {
  return state.setIn(['search', 'search'], value)
}

function setIsAddingRP(state, { isAdding, isFromCollection }) {
  const prevIsFromCollection = state.get('isFromCollection')
  const newIsFromCollection =
    isFromCollection === undefined ? prevIsFromCollection : isFromCollection
  return state
    .set('isAddingRP', isAdding)
    .set('isFromCollection', newIsFromCollection)
}

function setCurrentCollection(state, { collection }) {
  return state.set('currentCollection', fromJS(collection))
}

function resetAddCollection(state) {
  const stagedRps = state.get('stagedRPs')
  const collection = state.getIn(['search', 'referenceParameters'])

  // preseve any existing staged Rps
  const stagedRpsLessCollectionRps = stagedRps.filter(
    (rp) => !collection.some((crp) => crp.get('uri') === rp.get('uri'))
  )

  return state
    .set('selectedRPs', initialState.get('selectedRPs'))
    .set('stagedRPs', stagedRpsLessCollectionRps)
    .set('search', initialState.get('search'))
    .set('isAddingCollection', false)
}

function getRPsSuccess(state, { data }) {
  return state
    .setIn(['search', 'total'], data.total)
    .setIn(['search', 'referenceParameters'], fromJS(data.referenceParameters))
}

function setIsStagingRPs(state, { isStaging }) {
  return state.set('isStagingRPs', isStaging)
}

function setCommitNote(state, { commitNote }) {
  return state.set('commitNote', commitNote)
}

function setView(state, { view }) {
  return state.set('view', view)
}

function setIsAddingCollection(state, { isAddingCollection }) {
  return state.set('isAddingCollection', isAddingCollection)
}

function setIsPaginating(state) {
  return state.setIn(['search', 'isSearching'], true)
}

function setIsSearching(state, { isSearching }) {
  return state.setIn(['search', 'isSearching'], isSearching)
}

function startAddRPWorkflow(state, { serialNumber }) {
  return resetWorkflow(state)
    .set('started', true)
    .set('serialNumber', serialNumber)
}

function showServiceRecordModal(state, { visible }) {
  return state.setIn(['serviceRecordModal', 'visible'], visible)
}

function setSelectedRPs(state, { selectedRPs }) {
  if (!isArray(selectedRPs))
    return state.set('selectedRPs', List(fromJS([selectedRPs])))

  return state.set('selectedRPs', List(fromJS(selectedRPs)))
}

function showConfirmCircuitMappingModal(state, { visible }) {
  return state.setIn(['confirmCircuitMappingModal', 'visible'], visible)
}

function setPRSearchResults(state, { search }) {
  const transform = compose(
    set('isSearching', false),
    merge(fromJS(search)),
    get('search')
  )
  const searchResults = transform(state)
  return set('search', searchResults, state)
}

function resetAddRPWorkflow(state) {
  return resetWorkflow(state)
}

function setStagedRPs(state) {
  const selectedRPs = state.get('selectedRPs')
  const stagedRPs = state.get('stagedRPs')
  const newStagedRPs = selectedRPs.map((rp) => rp.set('staged', true))

  // this is a hack to get from add to review stages in collections
  const isFromCollection = state.get('isFromCollection')
  const nextStage = isFromCollection ? 'review' : isFromCollection

  return state
    .set('isFromCollection', nextStage)
    .set('isAddingRP', false)
    .set('stagedRPs', stagedRPs.concat(newStagedRPs))
}

function resetSearch(state) {
  return state
    .set('isStagingRPs', initialState.get('isStagingRPs'))
    .set('selectedRPs', initialState.get('selectedRPs'))
    .set('search', initialState.get('search'))
}

function removeStagedRP(state, { removedRP }) {
  return state.updateIn(['stagedRPs'], (rps) =>
    rps.filter((rp) => get('uri', rp) !== prop('uri', removedRP))
  )
}

function showPinsNotAvailableModal(state, { visible, circuitsByRPs }) {
  return state.set(
    'pinsNotAvailableModal',
    fromJS({
      visible,
      circuitsByRPs,
    })
  )
}

function resetWorkflow(state) {
  return state
    .set('started', initialState.get('started'))
    .set('serialNumber', initialState.get('serialNumber'))
    .set('pinsNotAvailableModal', initialState.get('pinsNotAvailableModal'))
    .set('search', initialState.get('search'))
    .set('stagedRPs', initialState.get('stagedRPs'))
    .set('selectedRP', initialState.get('selectedRP'))
    .set('view', initialState.get('view'))
    .set('isAddingCollection', false)
    .set('commitNote', initialState.get('commitNote'))
}

function getCollectionsSuccess(state, { groups }) {
  return state
    .set('isLoadingCollections', false)
    .set('collections', fromJS(groups))
}
